// src/components/Drive/AdminPanels/StatusPanel.jsx
import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { EditDeliveryModal } from "./EditDeliveryModal";
import {
  EyeOff,
  ArrowRight,
  ExternalLink,
  FileText,
  Undo2,
  UserPlus,
  Pencil,
  ChevronDown,
  ChevronUp,
  Trash2,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { ExpandedDeliveryRow } from "./ExpandedDeliveryRow";

import { useTheme } from "../../../context/ThemeContext";
import { useDelivery } from "../../../context/DeliveryContext";
import { printDeliveryWaybill } from "../../../utils/printUtils/index";
import { getTransportIcon } from "../../../utils/transportIcons";
import {
  revertDeliveryFn,
  assignDeliveryToMemberFn,
  deleteDeliveryFn,
} from "../../../firebase/deliveryFunctions";

/* ── Status chip config ── */

const STATUS_CONFIG = {
  active: { label: "Saatavilla", color: "info", section: "active" },
  ongoing: { label: "Meneillään", color: "primary", section: "inProgress" },
  picked: { label: "Noudettu", color: "warning", section: "inProgress" },
};

function getStatusChip(status) {
  const cfg = STATUS_CONFIG[status] || { label: status || "–", color: "default" };
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      sx={{ fontWeight: 600, fontSize: "0.7rem" }}
    />
  );
}

function canRevert(status) { return status === "ongoing" || status === "picked"; }
function canAssign(status) { return status === "active"; }

function fmtDate(val) {
  if (!val) return "–";
  try {
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("fi-FI", {
      day: "numeric",
      month: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return "–"; }
}

function truncate(str, len = 22) {
  if (!str) return "–";
  return str.length > len ? str.slice(0, len) + "…" : str;
}

function getSenderName(delivery, members) {
  if (delivery.senderInfo?.name) return delivery.senderInfo.name;
  if (delivery.senderId && members?.length > 0) {
    const member = members.find((m) => m.userId === delivery.senderId);
    if (member?.name) return member.name;
  }
  return "–";
}

/* ── MemberMenuItem ── */
function MemberMenuItem({ member }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Avatar sx={{ width: 24, height: 24, fontSize: "0.7rem" }}>
        {(member.name || "?").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)}
      </Avatar>
      <Box>
        <Typography variant="body2">{member.name || "Nimetön"}</Typography>
        {member.memberType === "owner" && (
          <Typography variant="caption" color="text.secondary">Omistaja</Typography>
        )}
      </Box>
    </Box>
  );
}

/* ── Route summary used in confirm dialogs ── */
function DeliveryRouteSummary({ from, to }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: "action.hover",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Typography variant="caption" fontWeight={600}>
        {truncate(from, 35)}
      </Typography>
      <ArrowRight size={12} opacity={0.4} />
      <Typography variant="caption">{truncate(to, 35)}</Typography>
    </Box>
  );
}

/* ── Desktop/mobile table ── */
function DeliveryTable({ deliveries, colors, members, onOpen, onRevert, onAssign, onEdit, onDelete, canPrintWaybill }) {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [expandedId, setExpandedId] = useState(null);

  const iconBtnSx = { p: isMobile ? 0.75 : 0.5 };
  const iconSize = isMobile ? 16 : 14;

  return (
    <TableContainer
      sx={{
        border: `1px solid ${colors.surface?.border || "rgba(0,0,0,0.12)"}`,
        borderRadius: 2,
        maxHeight: isMobile ? "calc(100vh - 220px)" : 420,
        overflowX: "auto",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", minWidth: isMobile ? 120 : 160 }}>
              Reitti
            </TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", minWidth: 90 }}>Tila</TableCell>
            {!isMobile && (
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Tyyppi</TableCell>
            )}
            {!isMobile && (
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Lähettäjä</TableCell>
            )}
            {!isMobile && (
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", minWidth: 70 }}>Aika</TableCell>
            )}
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }} align="center">
              Toiminnot
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deliveries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isMobile ? 3 : 6} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">Ei kuljetuksia.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            deliveries.map((d) => (
              <React.Fragment key={d.id}>
                {/* ── Main row ── */}
                <TableRow
                  hover
                  sx={{
                    cursor: "pointer",
                    opacity: d.hide ? 0.55 : 1,
                    "&:last-child td": { borderBottom: 0 },
                  }}
                  onClick={() =>
                    setExpandedId(expandedId === d.id ? null : d.id)
                  }
                >
                  {/* Route */}
                  <TableCell sx={{ maxWidth: isMobile ? 120 : 180 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                      <Tooltip title={d.originInfo?.address || d.origin || "–"} arrow>
                        <Typography variant="caption" fontWeight={600} noWrap>
                          {truncate(d.origin, isMobile ? 12 : 18)}
                        </Typography>
                      </Tooltip>
                      {getTransportIcon(d.deliveryTransportType)}
                      <Tooltip title={d.destinationInfo?.address || d.destination || "–"} arrow>
                        <Typography variant="caption" noWrap>
                          {truncate(d.destination, isMobile ? 12 : 18)}
                        </Typography>
                      </Tooltip>

                      {d.hide && (
                        <Tooltip title="Piilotettu" arrow>
                          <EyeOff size={12} opacity={0.4} />
                        </Tooltip>
                      )}
                      {expandedId === d.id
                        ? <ChevronUp size={12} opacity={0.4} />
                        : <ChevronDown size={12} opacity={0.4} />}
                    </Box>

                    {/* Times — mobile only, moved under the route so the
                        status column stays compact */}
                    {isMobile && (
                      <Box sx={{ display: "flex", flexDirection: "column", mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          {`Nouto ${fmtDate(d.pickupTime ?? d.pickUpTime)}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          {`Toimitus ${fmtDate(d.targetTime)}`}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, alignItems: "flex-start" }}>
                      {getStatusChip(d.status)}
                      {d?.courierName && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          {d.courierName}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Type — desktop only */}
                  {!isMobile && (
                    <TableCell>
                      {d.deliveryTransportType || d.packageSize ? (
                        <Chip
                          label={d.deliveryTransportType || d.packageSize}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.65rem", height: "20px" }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">–</Typography>
                      )}
                    </TableCell>
                  )}

                  {/* Sender — desktop only */}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="caption" fontWeight={600}>
                        {getSenderName(d, members)}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Time — desktop only */}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(d.pickupTime ?? d.pickUpTime)}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Actions — compact 2-column grid so rows stay short */}
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.25,
                        alignItems: "center",
                        justifyContent: "center",
                        maxWidth: isMobile ? 76 : 96,
                        mx: "auto",
                      }}
                    >
                      {/* Edit */}
                      <Tooltip title="Muokkaa" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onEdit(d); }}
                          sx={iconBtnSx}
                        >
                          <Pencil size={iconSize} opacity={0.6} />
                        </IconButton>
                      </Tooltip>

                      {/* Assign */}
                      {canAssign(d.status) && members?.length > 0 && (
                        <Tooltip title="Määritä jäsenelle" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onAssign(d); }}
                            sx={iconBtnSx}
                          >
                            <UserPlus size={iconSize} opacity={0.6} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Revert */}
                      {canRevert(d.status) && (
                        <Tooltip title="Peru kuljetus" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onRevert(d); }}
                            sx={iconBtnSx}
                          >
                            <Undo2 size={iconSize} opacity={0.6} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Print */}
                      {canPrintWaybill(d) && (
                        <Tooltip title="Tulosta rahtikirja" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); printDeliveryWaybill?.(d); }}
                            sx={iconBtnSx}
                          >
                            <FileText size={iconSize} opacity={0.6} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Delete */}
                      <Tooltip title="Poista pysyvästi" arrow>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => { e.stopPropagation(); onDelete(d); }}
                          sx={iconBtnSx}
                        >
                          <Trash2 size={iconSize} opacity={0.6} />
                        </IconButton>
                      </Tooltip>

                    </Box>
                  </TableCell>
                </TableRow>

                {/* ── Expanded detail row ── */}
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 6} sx={{ py: 0, border: 0 }}>
                    <Collapse in={expandedId === d.id} timeout="auto" unmountOnExit>
                      <ExpandedDeliveryRow delivery={d} members={members} />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1.5, pb: 1.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ExternalLink size={14} />}
                          onClick={(e) => { e.stopPropagation(); onOpen(d.id); }}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Avaa kuljetus
                        </Button>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ── Panel ── */
export function StatusPanel({ members = [], organizationData }) {
  const { allDeliveries, loading: deliveriesLoading } = useDelivery();
  const { colors } = useTheme();

  const [, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deliveryToEdit, setDeliveryToEdit] = useState(null);

  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [deliveryToRevert, setDeliveryToRevert] = useState(null);
  const [reverting, setReverting] = useState(false);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deliveryToAssign, setDeliveryToAssign] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(organizationData?.ownerId);
  const [assigning, setAssigning] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deliveryToDelete, setDeliveryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const deliverySections = useMemo(() => {
    if (!allDeliveries?.length) return { active: [], inProgress: [], completed: [] };
    const sections = { active: [], inProgress: [], completed: [] };
    allDeliveries.forEach((delivery) => {
      const section = STATUS_CONFIG[delivery.status]?.section || "completed";
      sections[section].push(delivery);
    });
    return sections;
  }, [allDeliveries]);

  const handleEditClick = (delivery) => { setDeliveryToEdit(delivery); setEditDialogOpen(true); };
  const handleEditClose = () => { setEditDialogOpen(false); setDeliveryToEdit(null); };

  const filteredDeliveries = useMemo(() => {
    if (!allDeliveries?.length) return [];
    if (statusFilter === "all") return allDeliveries;
    if (statusFilter === "hidden") return allDeliveries.filter((d) => d.hide === true);
    return allDeliveries.filter((d) => d.status === statusFilter);
  }, [allDeliveries, statusFilter]);

  const hiddenCount = useMemo(
    () => (allDeliveries || []).filter((d) => d.hide === true).length,
    [allDeliveries]
  );

  const filterOptions = [
    { value: "all", label: "Kaikki" },
    { value: "active", label: "Avoinna" },
    { value: "ongoing", label: "Meneillään" },
    { value: "picked", label: "Noudetut" },
    ...(hiddenCount > 0 ? [{ value: "hidden", label: `Piilotetut (${hiddenCount})` }] : []),
  ];

  const canPrintWaybill = (delivery) => delivery?.status !== "completed";

  const handleOpen = (deliveryId) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("deliveryId", deliveryId);
      p.delete("tab");
      return p;
    }, { replace: false });
  };

  const handleRevertClick = (delivery) => { setDeliveryToRevert(delivery); setRevertDialogOpen(true); };
  const handleRevertConfirm = async () => {
    if (!deliveryToRevert) return;
    try {
      setReverting(true);
      await revertDeliveryFn(deliveryToRevert.id);
      setRevertDialogOpen(false);
      setDeliveryToRevert(null);
    } catch (err) {
      console.error("Revert failed:", err);
      alert(err.message || "Virhe peruttaessa kuljetusta");
    } finally { setReverting(false); }
  };
  const handleRevertCancel = () => {
    if (!reverting) { setRevertDialogOpen(false); setDeliveryToRevert(null); }
  };

  const handleAssignClick = (delivery) => {
    setDeliveryToAssign(delivery); setSelectedMemberId(""); setAssignDialogOpen(true);
  };
  const handleAssignConfirm = async () => {
    if (!deliveryToAssign || !selectedMemberId) return;
    try {
      setAssigning(true);
      await assignDeliveryToMemberFn(deliveryToAssign.id, selectedMemberId);
      setAssignDialogOpen(false);
      setDeliveryToAssign(null);
      setSelectedMemberId("");
    } catch (err) {
      console.error("Assignment failed:", err);
      alert(err.message || "Virhe määritettäessä kuljetusta jäsenelle");
    } finally { setAssigning(false); }
  };
  const handleAssignCancel = () => {
    if (!assigning) { setAssignDialogOpen(false); setDeliveryToAssign(null); setSelectedMemberId(""); }
  };

  const handleDeleteClick = (delivery) => { setDeliveryToDelete(delivery); setDeleteDialogOpen(true); };
  const handleDeleteConfirm = async () => {
    if (!deliveryToDelete) return;
    try {
      setDeleting(true);
      await deleteDeliveryFn(deliveryToDelete.id);
      setDeleteDialogOpen(false);
      setDeliveryToDelete(null);
    } catch (err) {
      console.error("Delete failed:", err);
      alert(err.message || "Virhe poistettaessa kuljetusta");
    } finally { setDeleting(false); }
  };
  const handleDeleteCancel = () => {
    if (!deleting) { setDeleteDialogOpen(false); setDeliveryToDelete(null); }
  };

  const courierMembers = [
    ...members.filter((m) => m.memberType === "any"),
    ...members.filter((m) => m.memberType === "courier"),
  ];

  if (deliveriesLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const tableProps = {
    colors,
    members,
    onOpen: handleOpen,
    onRevert: handleRevertClick,
    onAssign: handleAssignClick,
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
    canPrintWaybill,
  };

  return (
    <Box>
      {/* ── Filter chips — single scrollable row so they never stack on mobile ── */}
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          mb: 1,
          overflowX: "auto",
          pb: 0.5,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {filterOptions.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            size="small"
            variant={statusFilter === opt.value ? "filled" : "outlined"}
            color={statusFilter === opt.value ? "primary" : "default"}
            onClick={() => setStatusFilter(opt.value)}
            sx={{ fontWeight: 600, fontSize: "0.7rem", cursor: "pointer", flexShrink: 0 }}
          />
        ))}
      </Box>

      {statusFilter === "all" ? (
        <Stack spacing={2.5}>
          {deliverySections.active.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                Saatavilla ({deliverySections.active.length})
              </Typography>
              <DeliveryTable deliveries={deliverySections.active} {...tableProps} />
            </Box>
          )}
          {deliverySections.inProgress.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                Meneillään ({deliverySections.inProgress.length})
              </Typography>
              <DeliveryTable deliveries={deliverySections.inProgress} {...tableProps} />
            </Box>
          )}
          {deliverySections.completed.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                Valmiit / Perutut ({deliverySections.completed.length})
              </Typography>
              <DeliveryTable deliveries={deliverySections.completed} {...tableProps} />
            </Box>
          )}
          {deliverySections.active.length === 0 &&
            deliverySections.inProgress.length === 0 &&
            deliverySections.completed.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 6 }}>
                Ei kuljetuksia.
              </Typography>
            )}
        </Stack>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            {filteredDeliveries.length} kuljetusta{hiddenCount > 0 && statusFilter === "hidden" ? "" : " (suodatettu)"}
          </Typography>
          <DeliveryTable deliveries={filteredDeliveries} {...tableProps} />
        </>
      )}

      {/* ── Revert Dialog ── */}
      <Dialog open={revertDialogOpen} onClose={handleRevertCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Peru kuljetus</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1.5, mb: 1 }}>
            Haluatko varmasti perua tämän kuljetuksen?
          </Typography>
          {deliveryToRevert && (
            <DeliveryRouteSummary
              from={deliveryToRevert.originInfo?.address || deliveryToRevert.from || deliveryToRevert.origin}
              to={deliveryToRevert.destinationInfo?.address || deliveryToRevert.to || deliveryToRevert.destination}
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Kuljetus vapautetaan muille kuljettajille ja palautetaan "Saatavilla" -tilaan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevertCancel} disabled={reverting}>Peruuta</Button>
          <Button
            onClick={handleRevertConfirm}
            variant="outlined"
            color="warning"
            disabled={reverting}
            startIcon={reverting ? <CircularProgress size={16} /> : <Undo2 size={16} />}
          >
            {reverting ? "Perutaan..." : "Vahvista"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Assignment Dialog ── */}
      <Dialog open={assignDialogOpen} onClose={handleAssignCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Määritä kuljetus jäsenelle</DialogTitle>
        <DialogContent>
          {deliveryToAssign && (
            <>
              <Box sx={{ mb: 2, mt: 1 }}>
                <DeliveryRouteSummary
                  from={deliveryToAssign.originInfo?.address || deliveryToAssign.from || deliveryToAssign.origin}
                  to={deliveryToAssign.destinationInfo?.address || deliveryToAssign.to || deliveryToAssign.destination}
                />
              </Box>
              <FormControl fullWidth>
                <InputLabel>Valitse kuljettaja</InputLabel>
                <Select
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  label="Valitse kuljettaja"
                  disabled={assigning}
                >
                  {courierMembers.map((member) => (
                    <MenuItem key={member.userId} value={member.userId}>
                      <MemberMenuItem member={member} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignCancel} disabled={assigning}>Peruuta</Button>
          <Button
            onClick={handleAssignConfirm}
            variant="contained"
            disabled={!selectedMemberId || assigning}
            startIcon={assigning ? <CircularProgress size={16} /> : <UserPlus size={16} />}
          >
            {assigning ? "Määritetään..." : "Määritä"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Poista kuljetus pysyvästi</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1.5, mb: 1 }}>
            Haluatko varmasti poistaa tämän kuljetuksen? Tätä toimintoa ei voi peruuttaa.
          </Typography>
          {deliveryToDelete && (
            <DeliveryRouteSummary
              from={deliveryToDelete.originInfo?.address || deliveryToDelete.from || deliveryToDelete.origin}
              to={deliveryToDelete.destinationInfo?.address || deliveryToDelete.to || deliveryToDelete.destination}
            />
          )}
          <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
            Kuljetus poistetaan kokonaan ja se häviää myös lähettäjältä ja kuljettajalta.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Peruuta</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Trash2 size={16} />}
          >
            {deleting ? "Poistetaan..." : "Poista"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Modal ── */}
      <EditDeliveryModal
        delivery={deliveryToEdit}
        open={editDialogOpen}
        onClose={handleEditClose}
      />
    </Box>
  );
}